import { ApiError } from '@iheartjane/data-access';
import type { Cart } from '@iheartjane/data-access';
import { cartMutationKeys, useCart } from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useIsMutating } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';

import { selectCart } from '@jane/monolith/customer/redux/cart';
import { selectEmbeddedApp } from '@jane/monolith/customer/redux/embeddedApp';
import { selectPosCart } from '@jane/monolith/customer/redux/posCart';
import { useCustomerSelector } from '@jane/monolith/customer/selectors';
import { getJwt } from '@jane/shared/auth';
import { useEcommApp } from '@jane/shared/providers';

import { updateLocalCartUuid, useLocalCartUuid } from './localCartUuid';
import { mapV1CartToV3 } from './mapV1CartToV3';
import type { V1CartInput } from './mapV1CartToV3';

/**
 * The read side of the cart facade, in the canonical v3 `Cart` shape.
 *
 * Behind `c3UseV3Cart` the cart comes from `GET /v3/carts/:uuid`, keyed by a uuid
 * from localStorage (see {@link useLocalCartUuid}); flag-off it comes from the v1
 * Redux slice through `mapV1CartToV3`. The returned shape is identical either way,
 * which is what makes the flip a body swap rather than a change at 20 call sites.
 *
 * Severance is still partial: `isMutating` folds in the v1 checkout flags, whose
 * write has not flipped yet.
 *
 * Which is why the flag must not be enabled until those flip too: no v1 thunk
 * invalidates `['cart', uuid]`, so v3 reads over v1 writes serve a stale cart.
 * That is broader than the flags above — several call sites still write a full
 * cart to the slice alone (`analyzeSpecials*`, `refreshCart`, `getRemoteCart`,
 * `replaceCart`). The converse holds too: the flipped writes update only the v3
 * cache, so slice-backed reads go stale — see item 17 in the phase-3 execution
 * plan.
 * TODO(carts-v3): remove the flag-off path once the v3 backing is the only source.
 */
export interface CartFacadeState {
  cart: Cart | null;
  /** Total item count (sum of product counts), not the distinct-line count. */
  cartSize: number;
  /**
   * The load has *settled* — not "a cart arrived". Flag-on this cannot be React
   * Query's `isFetched` alone: with no uuid the query is disabled and `isFetched`
   * stays `false` forever, hanging every consumer's initial-load gate on an empty
   * bag.
   */
  hasLoaded: boolean;
  hasSpecial: boolean;
  /**
   * True while a loyalty redemption started by `useCartActions().applyLoyalty`
   * is in flight. Deliberately narrower than `isMutating` (which folds in
   * checkout and four other flags): this backs the double-apply guard on the
   * reward buttons, so widening it would block a legitimate apply during an
   * unrelated cart mutation. Exposed on the facade — rather than left as a
   * direct `selectCart` read at each call site — so the guard and the write it
   * guards move together when `applyLoyalty` is repointed at the v3 mutation.
   */
  isApplyingLoyalty: boolean;
  /**
   * True while the reservation-mode PATCH is in flight, backing the summary and
   * checkout-total recalculating shimmers. Narrow on purpose — `isMutating`
   * folds in checkout and add-to-cart, which would shimmer the totals for
   * unrelated writes.
   *
   * The key is per-*hook* (`['cart', 'update']`), not per-intent, so this counts
   * any `useUpdateCart` — today only `updateReservationMode`. When
   * `updateDeliveryAddress` / `removeCartUser` move onto it (015c / 015d) this
   * starts reporting them too, which is the decided outcome: both recalculate
   * the totals, and `isRecalculating` (`cartDrawerSummary.tsx:80`) is what
   * consumers actually render. Do not split the key back out to re-narrow it,
   * and never narrow with a `variables` predicate — that couples this read to a
   * payload shape.
   */
  isChangingReservationMode: boolean;
  isFetching: boolean;
  /** An initial load only, unlike `isFetching`. */
  isLoading: boolean;
  /**
   * True while a *trackable* cart write is in flight. Composed here because the
   * v1 slice has no single mutation flag, so pending UI can be added in one place
   * rather than retrofitted per call site. Neither branch covers every action,
   * and they do not cover the same ones:
   *
   * - Flag-off: add-to-cart, checkout, loyalty-apply, reservation-mode change and
   *   unavailable-product deletion set slice flags; `updateProduct`,
   *   `removeProduct`, `removeLoyalty` and `updateDeliveryAddress` dispatch thunks
   *   that set none, so they are invisible.
   * - Flag-on: `useIsMutating` on the cart mutation base key, so every flipped
   *   write reports — including `updateProduct`, `removeProduct` and
   *   `removeLoyalty`, which flag-off cannot see. The exception is
   *   `deleteUnavailableProducts`, which calls the raw API fn to avoid a
   *   per-delete invalidate and so never enters the mutation cache. Unobservable
   *   today: every consumer gates an add-to-bag CTA that the unavailable-products
   *   alert has already replaced or covered. OR'd with the v1 checkout flags,
   *   whose write has not flipped yet (015e); that mixture is deliberate and ends
   *   when it does.
   */
  isMutating: boolean;
  /** Canonical order total consumers display. */
  orderTotal: number;
}

export const useCartState = (): CartFacadeState => {
  const useV3Cart = useFlag(FLAGS.c3UseV3Cart);
  const { appMode } = useEcommApp();
  // Only feeds the legacy-cart adoption read inside `useLocalCartUuid`, which is
  // also the only reason this slice is read here
  const { partnerId: storeId } = useCustomerSelector(selectEmbeddedApp);
  // Gated because the value is unused flag-off, and the read is not free
  const localCartUuid = useLocalCartUuid(appMode, storeId, !!useV3Cart);

  const {
    cart: v1Cart,
    isLoading,
    hasLoaded,
    isAddingItemToCart,
    isApplyingCrmRedemption,
    isChangingReservationModes,
    isCheckingOut,
    isDeletingUnavailableProducts,
    isFixingUnavailableCheckout,
  } = useCustomerSelector(selectCart);

  // `FetchCartProps` also accepts `deliveryAddress` / `paymentMethod`, deliberately
  // not passed: both are still Redux checkout draft, `cartKeys.detail` does not key
  // on the address (so a change would serve a stale cart), and omitting the payment
  // method keeps the key at the `['cart', uuid]` the v3 mutations write to. Delivery
  // fee and tax therefore compute without the address here until 015c.
  const v3Cart = useCart(
    { appMode, jwt: useV3Cart ? getJwt() : null, uuid: localCartUuid ?? '' },
    { enabled: !!useV3Cart }
  );

  // A 404 means the cart is gone — removing the last product destroys it
  // server-side (`@cart.destroy! if @cart.unfilled?`), so the invalidate-driven
  // refetch 404s. Dropping the uuid re-keys `useCart` to its disabled state and
  // every consumer re-renders to an empty bag; without it React Query keeps
  // serving the last good cart. Deliberately not `removeQueries`: that would kick
  // off a background refetch on the live observers. Source-agnostic on purpose —
  // checkout, expiry and another tab all land here too. Mirrors `useKioskCart`.
  const cartGone =
    !!useV3Cart &&
    !!localCartUuid &&
    v3Cart.error instanceof ApiError &&
    v3Cart.error.response.status === 404;

  useEffect(() => {
    if (cartGone) updateLocalCartUuid(appMode, storeId, null);
  }, [appMode, cartGone, storeId]);

  // POS totals are on their own slice, not the v1 cart, so consumers that branch on
  // `pos_cart.totals` take the same tax/total branch as the pre-facade path. Read
  // here rather than in the mapper, which stays Redux-free. The v3 payload carries
  // `pos_cart` itself, so this feeds flag-off only.
  const posCartTotals =
    useCustomerSelector(selectPosCart).posCart.totals ?? null;

  // A mutation's `isPending` cannot cross hook instances, so the in-flight flags
  // the v3 writes back are derived from the mutation cache by key. These filters
  // are *prefix* matches, so `all` (`['cart']`) counts every one of the ten cart
  // mutations — by design. Flag-off they all read 0 because `useCartActions`
  // returns the v1 branch: the hooks are still registered, but nothing invokes
  // them, and only a pending mutation is counted. That holds only while
  // `useCartActions` is the sole cart-mutation consumer in this tree — it is
  // today; `useCheckout` is reachable from kiosk, which has its own cache.
  const cartMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.all,
  });
  const addLoyaltyMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.addLoyaltyRedemption,
  });
  const updateCartMutationCount = useIsMutating({
    mutationKey: cartMutationKeys.update,
  });

  // An absent cart has an empty uuid (the slice's default). The cast crosses two
  // axes, both safe: the slice stores the cart `DeepReadonly` and the pure mapper
  // never mutates it, and the slice's runtime cart is the `@jane/shared/models`
  // shape, which `V1CartInput` models directly (widened ids + `amounts_manager`)
  // rather than papering over.
  const mappedV1Cart = useMemo(
    () =>
      !useV3Cart && v1Cart.uuid
        ? mapV1CartToV3(v1Cart as unknown as V1CartInput, posCartTotals)
        : null,
    [useV3Cart, v1Cart, posCartTotals]
  );

  const cart = useV3Cart ? (v3Cart.data ?? null) : mappedV1Cart;

  const cartSize = cart
    ? cart.products.reduce((total, product) => total + product.count, 0)
    : 0;

  const hasSpecial = Boolean(
    cart &&
    (cart.cart_discount_special_id != null ||
      cart.group_special_id != null ||
      cart.brand_discounts.length > 0 ||
      cart.products.some((product) => product.special_id != null))
  );

  return {
    cart,
    // With no uuid the query never runs, so "settled" is immediate.
    hasLoaded: useV3Cart ? !localCartUuid || v3Cart.isFetched : hasLoaded,
    isLoading: useV3Cart ? v3Cart.isLoading : isLoading && !hasLoaded,
    isFetching: useV3Cart ? v3Cart.isFetching : isLoading,
    // Every trackable in-flight flag that backs a facade action. Several facade
    // mutations set NO in-flight flag on the v1 slice and so cannot be reflected
    // here: `updateProduct` (`updateCart`) and `removeProduct` (`deleteCartItem`)
    // dispatch only their success actions; `removeLoyalty` (`removeCrmRedemption`)
    // toggles nothing; and `updateDeliveryAddress` (`initiateUpdateDeliveryAddress`)
    // sets neither a flag nor `isLoading`, so it is invisible to `isFetching` too.
    // These are genuinely untrackable on the Redux path — do not invent flags.
    //
    // Flag-on the composition is deliberately mixed, not an oversight: the
    // flipped writes report through the mutation cache, while checkout (015e) is
    // still a v1 thunk and only its slice flags can see it. `isAddingItemToCart`
    // is gone from this branch as of 015b — the v3 add and create both carry the
    // `['cart']` prefix, so `cartMutationCount` already covers them, and the
    // thunk that set the flag no longer runs. Drop the v1 half as checkout flips.
    isMutating: useV3Cart
      ? cartMutationCount > 0 || isCheckingOut || isFixingUnavailableCheckout
      : isAddingItemToCart ||
        isApplyingCrmRedemption ||
        isChangingReservationModes ||
        isCheckingOut ||
        isDeletingUnavailableProducts ||
        isFixingUnavailableCheckout,
    isApplyingLoyalty: useV3Cart
      ? addLoyaltyMutationCount > 0
      : isApplyingCrmRedemption,
    isChangingReservationMode: useV3Cart
      ? updateCartMutationCount > 0
      : isChangingReservationModes,
    cartSize,
    hasSpecial,
    orderTotal: cart?.order_total ?? 0,
  };
};
